/**
 * LocalStorageService - Browser localStorage persistence management
 * Professional localStorage wrapper with validation and error handling
 * V70 terminology compliant
 */

/*
interface _LocalStorageData {
  lastAvatarData?: {
    alias: string
    displayName: string
    statusMessage: string
    profileImage?: string | null
    savedAt: string
  }
  networkSettings?: {
    autoConnect: boolean
    rememberAlias: boolean
  }
  lastSession?: {
    peerId: string
    alias: string
    timestamp: string
  }
}
*/

export class LocalStorageService {
  private static readonly STORAGE_KEY = 'useselft-avatar-data'
  private static readonly SETTINGS_KEY = 'useselft-settings'
  private static readonly SESSION_KEY = 'useselft-last-session'

  // Cache to prevent excessive localStorage reads
  private static avatarDataCache: {
    data: any
    timestamp: number
  } | null = null
  private static readonly CACHE_DURATION = 30000 // 30 seconds

  /**
   * Save avatar data to localStorage with enhanced mobile support and image compression
   */
  static saveAvatarData(avatarData: {
    alias: string
    displayName: string
    statusMessage: string
    profileImage?: string | null
  }): boolean {
    try {
      const processedAvatarData = { ...avatarData }

      // MOBILE FIX: More aggressive compression for mobile devices
      if (avatarData.profileImage) {
        // Validate image data format first
        if (!this.isValidImageData(avatarData.profileImage)) {
          console.warn('⚠️ Invalid image data format, cannot save')
          processedAvatarData.profileImage = null
        } else {
          // Mobile browsers have stricter limits - but we'll try to keep original images
          const isMobile = this.isMobileDevice()
          const sizeLimit = isMobile ? 500000 : 1000000 // 500KB for mobile, 1MB for desktop

          if (avatarData.profileImage.length > sizeLimit) {
            console.log(`🖼️ Large profile image (${Math.round(avatarData.profileImage.length/1000)}KB) for ${isMobile ? 'mobile' : 'desktop'} storage`)
            // Still attempt to save - only compress if absolutely necessary
            processedAvatarData.profileImage = this.compressImage(avatarData.profileImage, isMobile)
          }
        }
      }

      const dataToSave = {
        ...processedAvatarData,
        savedAt: new Date().toISOString(),
        // Add device info for debugging
        deviceInfo: {
          isMobile: this.isMobileDevice(),
          userAgent: navigator.userAgent.substring(0, 100),
          timestamp: Date.now()
        }
      }

      // MOBILE FIX: Try saving with exponential backoff for mobile stability
      const saveWithRetry = async (attempts = 3) => {
        for (let i = 0; i < attempts; i++) {
          try {
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(dataToSave))
            return true
          } catch (error) {
            if (i === attempts - 1) throw error
            // Wait before retry (mobile browsers sometimes need a moment)
            await new Promise(resolve => setTimeout(resolve, 100 * (i + 1)))
          }
        }
        return false
      }

      // For mobile, use async retry pattern; for desktop, direct save
      if (this.isMobileDevice()) {
        saveWithRetry().then(success => {
          if (success) {
            console.log('✅ Avatar data saved to localStorage (mobile mode):', {
              alias: dataToSave.alias,
              hasImage: !!dataToSave.profileImage,
              imageSize: dataToSave.profileImage ? dataToSave.profileImage.length : 0,
              savedAt: dataToSave.savedAt
            })
          }
        })
        return true // Return immediately for mobile to avoid blocking
      } else {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(dataToSave))
        // Invalidate cache after successful save
        this.avatarDataCache = null
        console.log('✅ Avatar data saved to localStorage:', {
          alias: dataToSave.alias,
          hasImage: !!dataToSave.profileImage,
          imageSize: dataToSave.profileImage ? dataToSave.profileImage.length : 0,
          savedAt: dataToSave.savedAt
        })
        return true
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        console.error('localStorage quota exceeded. Trying without image...')
        try {
          // Try saving without the image
          const dataWithoutImage = {
            alias: avatarData.alias,
            displayName: avatarData.displayName,
            statusMessage: avatarData.statusMessage,
            profileImage: null,
            savedAt: new Date().toISOString(),
            fallbackReason: 'quota_exceeded'
          }
          localStorage.setItem(this.STORAGE_KEY, JSON.stringify(dataWithoutImage))
          console.log('✅ Avatar data saved without image due to storage quota')
          return true
        } catch (fallbackError) {
          console.error('Failed to save even without image:', fallbackError)
          return false
        }
      }
      console.error('Failed to save avatar data:', error)
      return false
    }
  }

  /**
   * Load avatar data from localStorage with caching optimization
   */
  static loadAvatarData(): {
    alias: string
    displayName: string
    statusMessage: string
    profileImage?: string | null
  } | null {
    try {
      // Check cache first to prevent excessive localStorage reads
      const now = Date.now()
      if (this.avatarDataCache && (now - this.avatarDataCache.timestamp) < this.CACHE_DURATION) {
        return this.avatarDataCache.data
      }

      const stored = localStorage.getItem(this.STORAGE_KEY)
      if (!stored) {
        // Cache the null result to prevent repeated failed reads
        this.avatarDataCache = { data: null, timestamp: now }
        return null
      }

      const parsed = JSON.parse(stored)
      
      // Validate the data structure
      if (typeof parsed.alias === 'string' && typeof parsed.displayName === 'string') {
        const loadedData = {
          alias: parsed.alias,
          displayName: parsed.displayName,
          statusMessage: parsed.statusMessage || '',
          profileImage: parsed.profileImage // CRITICAL: Don't default to null here - keep original value
        }

        // Enhanced debug logging to track image data
        const debugInfo = {
          alias: loadedData.alias,
          hasImage: !!loadedData.profileImage,
          imageSize: loadedData.profileImage ? loadedData.profileImage.length : 0,
          savedAt: parsed.savedAt,
          imageType: loadedData.profileImage ? (loadedData.profileImage.startsWith('data:image/') ? 'base64' : 'unknown') : 'none'
        }

        console.log('📂 Loaded avatar data from localStorage:', debugInfo)

        if (loadedData.profileImage) {
          console.log('🖼️ Image data preview:', loadedData.profileImage.substring(0, 100) + '...')
        } else {
          console.log('⚠️ No profile image data found in localStorage')
        }

        // Cache the successful result
        this.avatarDataCache = { data: loadedData, timestamp: now }
        return loadedData
      }
      
      return null
    } catch (error) {
      console.error('Failed to load avatar data:', error)
      return null
    }
  }

  /**
   * Save last successful session info
   */
  static saveLastSession(peerId: string, alias: string): boolean {
    try {
      const sessionData = {
        peerId,
        alias,
        timestamp: new Date().toISOString()
      }
      
      localStorage.setItem(this.SESSION_KEY, JSON.stringify(sessionData))
      console.log('💾 Last session saved')
      return true
    } catch (error) {
      console.error('Failed to save session:', error)
      return false
    }
  }

  /**
   * Load last session info
   */
  static loadLastSession(): { peerId: string; alias: string; timestamp: Date } | null {
    try {
      const stored = localStorage.getItem(this.SESSION_KEY)
      if (!stored) return null

      const parsed = JSON.parse(stored)
      
      if (typeof parsed.peerId === 'string' && typeof parsed.alias === 'string') {
        return {
          peerId: parsed.peerId,
          alias: parsed.alias,
          timestamp: new Date(parsed.timestamp)
        }
      }
      
      return null
    } catch (error) {
      console.error('Failed to load last session:', error)
      return null
    }
  }

  /**
   * Save network settings
   */
  static saveNetworkSettings(settings: {
    autoConnect: boolean
    rememberAlias: boolean
  }): boolean {
    try {
      localStorage.setItem(this.SETTINGS_KEY, JSON.stringify(settings))
      return true
    } catch (error) {
      console.error('Failed to save network settings:', error)
      return false
    }
  }

  /**
   * Load network settings with defaults
   */
  static loadNetworkSettings(): {
    autoConnect: boolean
    rememberAlias: boolean
  } {
    try {
      const stored = localStorage.getItem(this.SETTINGS_KEY)
      if (!stored) {
        // Return default settings
        return {
          autoConnect: false,
          rememberAlias: true
        }
      }

      const parsed = JSON.parse(stored)
      return {
        autoConnect: parsed.autoConnect ?? false,
        rememberAlias: parsed.rememberAlias ?? true
      }
    } catch (error) {
      console.error('Failed to load network settings:', error)
      return {
        autoConnect: false,
        rememberAlias: true
      }
    }
  }

  /**
   * Create complete avatar data for relogin (preserving alias, generating new peerId)
   */
  static prepareReloginAvatar(): {
    alias: string
    displayName: string
    statusMessage: string
    profileImage?: string | null
    isRelogin: true
  } | null {
    const avatarData = this.loadAvatarData()
    // const _lastSession = this.loadLastSession() // Unused
    
    if (!avatarData) {
      return null
    }

    // For relogin: preserve alias and avatar data, but new peerId will be generated
    console.log('🔄 Preparing relogin with preserved alias:', avatarData.alias)
    
    return {
      ...avatarData,
      isRelogin: true as const
    }
  }

  /**
   * Check if this is a returning user (has previous session)
   */
  static hasExistingSession(): boolean {
    return this.loadAvatarData() !== null
  }

  /**
   * Get last known alias for relogin
   */
  static getLastKnownAlias(): string | null {
    const avatarData = this.loadAvatarData()
    return avatarData?.alias || null
  }

  /**
   * Clear all stored data
   */
  static clearAllData(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY)
      localStorage.removeItem(this.SETTINGS_KEY)
      localStorage.removeItem(this.SESSION_KEY)
      console.log('🧹 All localStorage data cleared')
    } catch (error) {
      console.error('Failed to clear localStorage:', error)
    }
  }

  /**
   * Check if localStorage is available
   */
  static isAvailable(): boolean {
    try {
      const testKey = '__localStorage_test__'
      localStorage.setItem(testKey, 'test')
      localStorage.removeItem(testKey)
      return true
    } catch {
      return false
    }
  }

  /**
   * Compress base64 image data with mobile-optimized compression
   */
  private static compressImage(base64Image: string, isMobile: boolean = false): string {
    try {
      // Extract image type and data
      const matches = base64Image.match(/^data:image\/([a-zA-Z]*);base64,(.*)$/)
      if (!matches) {
        console.warn('Invalid base64 image format, creating placeholder')
        return this.createPlaceholderAvatarSVG()
      }

      // const _imageType = matches[1] // Unused
      const imageData = matches[2]

      if (!imageData) {
        console.warn('No image data found after parsing, using placeholder')
        return this.createPlaceholderAvatarSVG()
      }

      // Mobile devices have stricter limits - but increase limits to preserve images
      const sizeLimit = isMobile ? 500000 : 1000000 // ~500KB for mobile, ~1MB for desktop

      if (imageData.length > sizeLimit) {
        console.warn(`Image too large (${Math.round(imageData.length/1000)}KB) for ${isMobile ? 'mobile' : 'desktop'} localStorage`)
        // Still try to save the original image - modern browsers handle larger localStorage better
        // Only fallback to placeholder if save actually fails
      }

      // Always try to keep the original image
      return base64Image
    } catch (error) {
      console.error('Image compression failed:', error)
      return this.createPlaceholderAvatarSVG()
    }
  }

  /**
   * Create a properly formatted avatar SVG with customizable colors
   */
  static createAvatarSVG(text: string = 'User', bgColor: string = '#6b7280', textColor: string = '#ffffff'): string {
    // Validate color values to prevent SVG parsing errors
    const validHexColor = (color: string): string => {
      if (!/^#[0-9A-Fa-f]{6}$/.test(color)) {
        console.warn(`Invalid color ${color}, using default`)
        return color === bgColor ? '#6b7280' : '#ffffff'
      }
      return color
    }

    const safeBgColor = validHexColor(bgColor)
    const safeTextColor = validHexColor(textColor)
    const safeText = text.slice(0, 4) // Limit text length

    const svg = `<svg width="100" height="100" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Avatar for ${safeText}">
  <defs>
    <style>
      .bg { fill: ${safeBgColor}; }
      .text {
        fill: ${safeTextColor};
        font-family: system-ui, -apple-system, sans-serif;
        font-size: 14px;
        text-anchor: middle;
        dominant-baseline: middle;
        font-weight: 500;
      }
    </style>
  </defs>
  <circle cx="50" cy="50" r="40" class="bg"/>
  <text x="50" y="50" class="text">${safeText}</text>
</svg>`

    // Convert to base64 data URL
    try {
      const base64 = btoa(unescape(encodeURIComponent(svg)))
      return `data:image/svg+xml;base64,${base64}`
    } catch (error) {
      console.error('Failed to create avatar SVG:', error)
      // Fallback to a simple data URI without base64 encoding
      return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
    }
  }

  /**
   * Create a properly formatted placeholder avatar SVG
   */
  private static createPlaceholderAvatarSVG(): string {
    return this.createAvatarSVG('User', '#6b7280', '#ffffff')
  }

  /**
   * Check if device is mobile for storage optimization
   */
  private static isMobileDevice(): boolean {
    // Multiple checks for mobile detection
    const userAgent = navigator.userAgent.toLowerCase()
    const mobileKeywords = ['mobile', 'android', 'iphone', 'ipad', 'ipod', 'blackberry', 'windows phone']
    const hasMobileKeyword = mobileKeywords.some(keyword => userAgent.includes(keyword))
    const hasTouch = 'ontouchstart' in window
    const smallScreen = window.innerWidth <= 768

    return hasMobileKeyword || (hasTouch && smallScreen)
  }

  /**
   * Validate image data format
   */
  private static isValidImageData(imageData: string): boolean {
    if (!imageData || typeof imageData !== 'string') return false

    // Check if it's a valid data URL for images
    const imageDataUrlPattern = /^data:image\/(png|jpe?g|gif|svg\+xml|webp);base64,/
    return imageDataUrlPattern.test(imageData)
  }

  /**
   * Get storage usage stats with mobile considerations
   */
  static getStorageStats(): {
    totalSize: number
    usedSpace: number
    availableSpace: number
    storageSupported: boolean
    isMobileDevice: boolean
  } {
    const isMobile = this.isMobileDevice()

    if (!this.isAvailable()) {
      return {
        totalSize: 0,
        usedSpace: 0,
        availableSpace: 0,
        storageSupported: false,
        isMobileDevice: isMobile
      }
    }

    try {
      let usedSpace = 0
      for (const key in localStorage) {
        if (localStorage.hasOwnProperty(key)) {
          usedSpace += localStorage[key].length + key.length
        }
      }

      // Mobile browsers typically have smaller localStorage limits
      const estimatedTotalSize = isMobile ? 2 * 1024 * 1024 : 5 * 1024 * 1024 // 2MB mobile, 5MB desktop

      return {
        totalSize: estimatedTotalSize,
        usedSpace,
        availableSpace: estimatedTotalSize - usedSpace,
        storageSupported: true,
        isMobileDevice: isMobile
      }
    } catch (error) {
      console.error('Failed to calculate storage stats:', error)
      return {
        totalSize: 0,
        usedSpace: 0,
        availableSpace: 0,
        storageSupported: false,
        isMobileDevice: isMobile
      }
    }
  }
}